import { Avatar, Hovercard, Icon, useHovercardState } from "@resource/atlas";
import {
  atlasCircleChecked,
  atlasRing,
  atlasRingInfo,
} from "@resource/atlas/icons";
import clsx from "clsx";
import { Skeleton, SkeletonCollection } from "components/Skeleton";
import { InterviewPlanItemFragment } from "generated/schemaTypes";
import { ComponentPropsWithoutRef, ReactNode } from "react";
import { useResponsiveValue } from "utils/responsive";
import { WithoutTypename } from "utils/types";

type InterviewPlanItem = WithoutTypename<InterviewPlanItemFragment>;

type InterviewPlanItemState = "completed" | "current" | "locked";

export type InterviewItemActionProps = {
  item: InterviewPlanItem;
  state: InterviewPlanItemState;
};

type InterviewPlanItemProps = ComponentPropsWithoutRef<"li"> & {
  interviewPlanItem: InterviewPlanItem;
  state: InterviewPlanItemState;
  candidate?: {
    name: string;
    imageUrl?: string;
  };
  position: number;
  hideDescriptionsOnHover?: boolean;
  hideInfoIcon?: boolean;
  getItemAction?: (props: InterviewItemActionProps) => ReactNode;
  cancelled?: boolean;
};

function InterviewPlanViewItem({
  interviewPlanItem,
  state,
  candidate,
  position,
  hideDescriptionsOnHover,
  hideInfoIcon,
  getItemAction,
  cancelled,
  ...props
}: InterviewPlanItemProps) {
  const placement = useResponsiveValue<"left-start" | "bottom">({
    xs: "bottom",
    lg: "left-start",
  });

  let indicator: ReactNode;
  // eslint-disable-next-line default-case
  switch (state) {
    case "completed":
      indicator = (
        <Icon content={atlasCircleChecked} className="text-success-500" />
      );
      break;
    case "current":
      indicator = (
        <span
          className={clsx(
            "rounded-full ml-[-0.25rem]",
            !cancelled && " border-2 border-red-500"
          )}
        >
          <Avatar
            name={candidate?.name}
            image={candidate?.imageUrl}
            size="xs"
            className="border-2 border-white"
          />
        </span>
      );
      break;
    case "locked":
      indicator = (
        <div className="w-5 h-5 pr-5 relative text-subtle inline">
          <Icon className="absolute inset-0" content={atlasRing} />
          <span className="absolute inset-0 text-body-sm text-[.625rem] text-center w-5 leading-5">
            {position}
          </span>
        </div>
      );
      break;
  }

  const enableHover =
    !hideDescriptionsOnHover && !!interviewPlanItem.description;

  const hovercard = useHovercardState({
    placement,
  });

  const onClick = () => {
    if (enableHover) hovercard.setOpen(!hovercard.open);
  };

  return (
    <Hovercard.Root state={hovercard}>
      <Hovercard.Anchor>
        <li
          role="presentation"
          key={interviewPlanItem.id}
          className={clsx(
            "flex items-center w-full px-2 transition-colors rounded-md group",
            {
              "py-2.5": state !== "current",
              "font-bold py-1.5": state === "current",
              "hover:bg-light-gray-500": enableHover,
              "text-disabled": cancelled,
            }
          )}
          {...(enableHover
            ? {
                onKeyDown: onClick,
                onClick,
              }
            : {})}
          {...props}
        >
          {indicator}{" "}
          <div
            className={clsx("truncate", {
              "ml-2": state !== "current",
              "ml-[0.3125em]": state === "current",
              "line-through": cancelled && state === "locked",
            })}
          >
            {interviewPlanItem.title}
          </div>
          <div className="flex ml-auto">
            {enableHover && !hideInfoIcon && (
              <Icon content={atlasRingInfo} className="w-5 h-5 text-subtle" />
            )}
            {getItemAction
              ? getItemAction({ item: interviewPlanItem, state })
              : null}
          </div>
        </li>
      </Hovercard.Anchor>
      {enableHover && (
        <Hovercard.Content>
          <Hovercard.Heading className="text-body-md-heavy mb-1">
            {interviewPlanItem.title}
          </Hovercard.Heading>
          <Hovercard.Description className="text-body-md">
            {interviewPlanItem.description}
          </Hovercard.Description>
        </Hovercard.Content>
      )}
    </Hovercard.Root>
  );
}

export type InterviewPlanViewProps = {
  interviewPlan: {
    items: InterviewPlanItem[];
  };
  isLoading?: boolean;
  candidate?: {
    name: string;
    imageUrl: string;
  };
  currentInterviewPlanItemId?: string | null;
  hideDescriptionsOnHover?: boolean;
  hideInfoIcon?: boolean;
  getItemAction?: InterviewPlanItemProps["getItemAction"];
  cancelled?: boolean;
};

export function InterviewPlanView({
  interviewPlan,
  currentInterviewPlanItemId,
  hideDescriptionsOnHover,
  hideInfoIcon,
  candidate,
  isLoading,
  getItemAction,
  cancelled,
}: InterviewPlanViewProps) {
  const currentInterviewPlanItemIndex = interviewPlan.items.findIndex(
    (item) => item.id === currentInterviewPlanItemId
  );

  return (
    <div className={clsx(cancelled && "opacity-70")}>
      {!isLoading ? (
        <ul className="bg-light-gray-200 rounded-lg p-2 text-body-md">
          {interviewPlan.items.map((interviewPlanItem, index) => {
            let state: InterviewPlanItemState;
            if (
              currentInterviewPlanItemIndex &&
              index < currentInterviewPlanItemIndex
            ) {
              state = "completed";
            } else if (index === currentInterviewPlanItemIndex) {
              state = "current";
            } else {
              state = "locked";
            }

            return (
              <InterviewPlanViewItem
                key={interviewPlanItem.id}
                interviewPlanItem={interviewPlanItem}
                state={state}
                position={index + 1}
                hideDescriptionsOnHover={hideDescriptionsOnHover}
                hideInfoIcon={hideInfoIcon}
                candidate={candidate}
                getItemAction={getItemAction}
                cancelled={cancelled}
              />
            );
          })}
        </ul>
      ) : (
        <SkeletonCollection
          className="flex flex-col space-y-4 justify-center items-start bg-light-gray-100 rounded-md p-5"
          count={5}
        >
          <div className="flex flex-row items-center">
            <Skeleton className="w-5 h-5 mr-2" type="circle" variant="dark" />
            <Skeleton className="w-48 h-[14px]" type="text" variant="dark" />
          </div>
        </SkeletonCollection>
      )}
    </div>
  );
}
